import React, { useRef, useState } from "react";
import PlayerImage from "../../../components/PlayerImage";
import { baseURL } from "../../../Constants";
import { ColumnColor, colorVars, initialOf } from "./contentTeamShared";
import styles from "./ContentTeam.module.css";

/** The league CDN's no-photo silhouette ignores the requested width and is always 687 px square. */
const PLACEHOLDER_PX = 687;

const SIZES = { sm: 22, md: 44, lg: 60 } as const;

/** Matches the scale in `.photoAvatar img`: a gap at the top of the photo grows by this much. */
const ZOOM = 1.75;

/**
 * How far down the photo the subject starts, as a fraction of its height (0 = touching the top).
 * League photos start at the top edge, which the avatar's framing is tuned for; a photo uploaded
 * here can leave room above the head and would sit too low. Only our API allows reading the pixels
 * (the league CDN sends no CORS header), and the league photos don't need it, so anything else is 0.
 */
const probeTop = (src: string): Promise<number> =>
  new Promise((resolve) => {
    if (!src.startsWith(baseURL)) return resolve(0);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const size = 64;
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        if (!ctx) return resolve(0);
        ctx.drawImage(img, 0, 0, size, size);
        const { data } = ctx.getImageData(0, 0, size, size);
        for (let y = 0; y < size; y++) {
          for (let x = 0; x < size; x++) {
            if (data[(y * size + x) * 4 + 3] > 32) return resolve(Math.min(y / size, 0.35));
          }
        }
        resolve(0);
      } catch {
        resolve(0);
      }
    };
    img.onerror = () => resolve(0);
    img.src = src;
  });

interface Props {
  steamId: string;
  name: string;
  color: ColumnColor;
  size: keyof typeof SIZES;
}

/**
 * A predictor's head in a ring of their colour: the league photo cropped to head and shoulders.
 * The photo stays hidden until it has loaded, and the coloured initial shows instead when there is
 * no real photo (the silhouette placeholder, or nothing at all).
 */
const HostAvatar: React.FC<Props> = ({ steamId, name, color, size }) => {
  const [state, setState] = useState<{ id: string; status: "photo" | "none"; top: number } | null>(null);
  const status = state?.id === steamId ? state.status : "loading";
  const top = state?.id === steamId ? state.top : 0;
  // A probe that finishes after the avatar moved on to another player must not overwrite it.
  const currentId = useRef(steamId);
  currentId.current = steamId;
  const px = SIZES[size];

  return (
    <span className={`${styles.photoAvatar} ${styles[`photo_${size}`]}`} style={colorVars(color)} aria-hidden="true">
      {status !== "photo" && <span className={styles.photoInitial}>{initialOf(name)}</span>}
      {status !== "none" && (
        <PlayerImage
          id={steamId}
          width={px * 2}
          imgStyle={{ opacity: status === "photo" ? 1 : 0, "--lift": `${top * ZOOM * 100}%` } as React.CSSProperties}
          onLoad={(e) => {
            const img = e.currentTarget;
            if (img.naturalWidth === PLACEHOLDER_PX && img.naturalHeight === PLACEHOLDER_PX) {
              setState({ id: steamId, status: "none", top: 0 });
              return;
            }
            // Shown once the gap above the subject is known, so the photo never jumps up.
            probeTop(img.currentSrc || img.src).then((gap) => {
              if (currentId.current === steamId) setState({ id: steamId, status: "photo", top: gap });
            });
          }}
          onUnavailable={() => setState({ id: steamId, status: "none", top: 0 })}
        />
      )}
    </span>
  );
};

export default HostAvatar;
